import flattenDeep from "lodash/flattenDeep";
import isEmpty from "lodash/isEmpty";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { TreeNode } from "./types";

type FlatTreeNode<T extends Record<string, unknown>> = {
  source: TreeNode<T>;
  parentKey?: string;
};

type KeyboardNavState = {
  focusedKey: string;
  handleKeydown: (e: KeyboardEvent) => void;
  resetFocusable: () => void;
};

/**
 * Handles keyboard navigation for a tree component as described in this spec:
 * https://www.w3.org/TR/2021/NOTE-wai-aria-practices-1.2-20211129/examples/treeview/treeview-navigation.html
 *
 * Functionalities not yet implemented:
 * - Handling [a-zA-Z]: Focus to the next node whose name starts with the typed character.
 * - Handling asterisk: Expand all closed sibling nodes
 */
export default function useKeyboardNav<T extends Record<string, unknown>>(
  treeData: TreeNode<T>[],
  selectedNodeKey: string | undefined,
  setSelectedNodeKey: (key: string | undefined) => void,
  openedNodeKeys: Record<string, boolean>,
  setOpenedNodeKeys: Dispatch<SetStateAction<Record<string, boolean>>>
): KeyboardNavState {
  const isOpen = useCallback(
    (node: TreeNode<T>) => openedNodeKeys[node.key] ?? false,
    [openedNodeKeys]
  );
  const isVisible = useCallback(
    (node: FlatTreeNode<T>) =>
      node.parentKey === undefined || openedNodeKeys[node.parentKey] === true,
    [openedNodeKeys]
  );
  const isRootNode = useCallback(
    (flatTreeNode: FlatTreeNode<T>) => flatTreeNode.parentKey === undefined,
    []
  );
  const hasChildren = useCallback(
    <U extends { children?: unknown }>(node: U) => node.children !== undefined,
    []
  );

  const flatten = useCallback(
    (
      node: TreeNode<T>,
      parentKey: string | undefined
      // Typing recursive is hard.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): [FlatTreeNode<T>, any] | [FlatTreeNode<T>] => {
      const flatTreeNode = { parentKey, source: node };
      return hasChildren(node)
        ? [flatTreeNode, node.children?.map((d) => flatten(d, node.key))]
        : [flatTreeNode];
    },
    [hasChildren]
  );

  const flatRepresentation = useMemo(
    () => flattenDeep<FlatTreeNode<T>>(treeData.map((d) => flatten(d, undefined))),
    [flatten, treeData]
  );

  const getInitialFocus = useCallback(() => {
    if (treeData.length === 0) {
      return { index: -1, key: "" };
    }

    if (selectedNodeKey === undefined || isEmpty(selectedNodeKey)) {
      return { index: 0, key: treeData[0].key };
    }

    const selectedNodeIndex = flatRepresentation.findIndex((n) => n.source.key === selectedNodeKey);

    return {
      index: selectedNodeIndex,
      // Maynot be there when the tree is dismounted.
      key: flatRepresentation[selectedNodeIndex]?.source.key,
    };
  }, [flatRepresentation, treeData, selectedNodeKey]);

  const [currentFocus, setCurrentFocus] = useState<{ index: number; key: string }>(getInitialFocus);

  // TODO: Calculating focus twice on mount. To optimize, we can:
  //   1. Initialize currentFocus with undefined then set it with an effect, or
  //   2. Run this effect on subsequent renders.
  useEffect(() => {
    setCurrentFocus(getInitialFocus());
  }, [getInitialFocus]);

  const getNextFocusableNode = useCallback(
    (currentFocusIndex: number) => {
      let nextIndex = currentFocusIndex;
      let nextNode = undefined;
      do {
        nextIndex = (nextIndex + 1) % flatRepresentation.length;
        nextNode = flatRepresentation[nextIndex];
      } while (!isVisible(nextNode));
      return { index: nextIndex, node: nextNode };
    },
    [flatRepresentation, isVisible]
  );

  const getPreviousFocusableNode = useCallback(
    (currentFocusIndex: number) => {
      let previousIndex = currentFocusIndex;
      let previousNode = undefined;
      do {
        previousIndex = previousIndex === 0 ? flatRepresentation.length - 1 : previousIndex - 1;
        previousNode = flatRepresentation[previousIndex];
      } while (!isVisible(previousNode));
      return { index: previousIndex, node: previousNode };
    },
    [flatRepresentation, isVisible]
  );

  const focusOnFirstNode = useCallback(() => {
    const firstNodeKey = flatRepresentation[0].source.key;
    setCurrentFocus({ index: 0, key: firstNodeKey });
    document.getElementById(`treeitem-${firstNodeKey}`)?.focus();
  }, [flatRepresentation]);

  const focusOnLastFocusableNode = useCallback(() => {
    const { index, node } = getPreviousFocusableNode(0);
    setCurrentFocus({ index, key: node.source.key });
    document.getElementById(`treeitem-${node.source.key}`)?.focus();
  }, [getPreviousFocusableNode]);

  const focusOnNextNode = useCallback(() => {
    const { index, node } = getNextFocusableNode(currentFocus.index);
    setCurrentFocus({ index, key: node.source.key });
    document.getElementById(`treeitem-${node.source.key}`)?.focus();
  }, [currentFocus.index, getNextFocusableNode]);

  const focusOnPreviousNode = useCallback(() => {
    const { index, node } = getPreviousFocusableNode(currentFocus.index);
    setCurrentFocus({ index, key: node.source.key });
    document.getElementById(`treeitem-${node.source.key}`)?.focus();
  }, [currentFocus.index, getPreviousFocusableNode]);

  const focusOnFirstChildNode = useCallback(() => {
    const { index, node } = getNextFocusableNode(currentFocus.index);
    if (node.parentKey === currentFocus.key) {
      setCurrentFocus({ index, key: node.source.key });
      document.getElementById(`treeitem-${node.source.key}`)?.focus();
    }
  }, [getNextFocusableNode, currentFocus.index, currentFocus.key]);

  const focusOnParentNode = useCallback(() => {
    const focusedNode = flatRepresentation[currentFocus.index];
    const parentNodeIndex = flatRepresentation.findIndex(
      (n) => n.source.key === focusedNode.parentKey
    );
    const parentNode = flatRepresentation[parentNodeIndex];

    setCurrentFocus({
      index: parentNodeIndex,
      key: parentNode.source.key,
    });

    document.getElementById(`treeitem-${parentNode.source.key}`)?.focus();
  }, [flatRepresentation, currentFocus.index]);

  const handleKeydown = useCallback(
    (e) => {
      switch (e.code) {
        case "ArrowUp": {
          focusOnPreviousNode();
          break;
        }
        case "ArrowDown": {
          focusOnNextNode();
          break;
        }
        case "ArrowRight": {
          const focusedNode = flatRepresentation[currentFocus.index];
          if (!hasChildren(focusedNode.source)) return;
          if (isOpen(focusedNode.source)) {
            focusOnFirstChildNode();
          } else {
            setOpenedNodeKeys({ ...openedNodeKeys, [focusedNode.source.key]: true });
          }
          break;
        }
        case "ArrowLeft": {
          const focusedNode = flatRepresentation[currentFocus.index];
          if (!hasChildren(focusedNode.source) || !isOpen(focusedNode.source)) {
            if (isRootNode(focusedNode)) return;
            focusOnParentNode();
          }
          setOpenedNodeKeys({ ...openedNodeKeys, [focusedNode.source.key]: false });
          break;
        }
        case "Home": {
          focusOnFirstNode();
          break;
        }
        case "End": {
          focusOnLastFocusableNode();
          break;
        }
        case "Enter":
        case "Space": {
          setSelectedNodeKey(currentFocus.key);
          break;
        }
        default:
          break;
      }
    },
    [
      currentFocus,
      flatRepresentation,
      focusOnFirstChildNode,
      focusOnFirstNode,
      focusOnLastFocusableNode,
      focusOnNextNode,
      focusOnParentNode,
      focusOnPreviousNode,
      hasChildren,
      isOpen,
      isRootNode,
      openedNodeKeys,
      setOpenedNodeKeys,
      setSelectedNodeKey,
    ]
  );

  const resetFocusable = useCallback(() => {
    setCurrentFocus(getInitialFocus());
  }, [getInitialFocus]);

  return {
    focusedKey: currentFocus.key,
    handleKeydown,
    resetFocusable,
  };
}
